import React, { useState, useEffect, useContext } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Autocomplete,
  Paper,
  ListItemAvatar,
  Switch,
  Grow,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Groups as GroupsIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Calculate as CalculateIcon,
  Star as StarIcon,
  Assessment as AssessmentIcon,
  RocketLaunch,
  AutoAwesome,
  Celebration,
  LocalFireDepartment,
  BarChart,
  CardGiftcard,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon
} from '@mui/icons-material';
import { SettingsContext } from '../../contexts/SettingsContext';
import { useReservationsData, useClientsData, usePackagesData, createResource, updateResource, deleteResource } from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Reservations = () => {
  const { settings, formatCurrency, formatDate, calculateTotalWithTax } = useSettings();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState('all'); // Pour la filtration rapide
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const showQuickActions = false;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get data from data services with real-time hooks
  const { reservations: reservationsData, loading: reservationsLoading } = useReservationsData();
  const { clients: clientsData, loading: clientsLoading } = useClientsData();
  const { packages: packagesData, loading: packagesLoading } = usePackagesData();
  
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  // Add new state for details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(null);

  // Add form data state
  const [formData, setFormData] = useState({
    clientId: null,
    clientNom: '',
    clientEmail: '',
    destination: '',
    forfait: '',
    forfaitId: null,
    dateDepart: '',
    dateRetour: '',
    nombrePassagers: 1,
    prixUnitaire: 0,
    prixTotal: 0,
    acompte: 0,
    statut: 'En attente',
    statutPaiement: 'En attente',
    notes: '',
    enableTVA: true,
    customTVARate: settings.taxRate
  });

  // Chargement optimisé - affichage immédiat de l'interface des réservations
  const isLoading = false;

  // Load initial data and apply filters when data or filters change
  useEffect(() => {
    if (reservationsLoading || !reservationsData) return;
    
    let filtered = reservationsData.filter(reservation => {
      const matchesSearch = 
        reservation.clientNom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.forfait?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || reservation.statut === filterStatus;
      
      let matchesPeriod = true;
      if (filterPeriod !== 'all') {
        const today = new Date();
        const reservationDate = new Date(reservation.dateDepart);
        
        switch (filterPeriod) {
          case 'thisMonth':
            matchesPeriod = reservationDate.getMonth() === today.getMonth() && 
                          reservationDate.getFullYear() === today.getFullYear();
            break;
          case 'nextMonth':
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1);
            matchesPeriod = reservationDate.getMonth() === nextMonth.getMonth() && 
                          reservationDate.getFullYear() === nextMonth.getFullYear();
            break;
          case 'upcoming':
            matchesPeriod = reservationDate >= today;
            break;
          default:
            matchesPeriod = true;
        }
      }
      
      // Filtration rapide par onglet
      let matchesActiveTab = true;
      if (activeTab === 'confirmee') matchesActiveTab = reservation.statut === 'Confirmée';
      else if (activeTab === 'attente') matchesActiveTab = reservation.statut === 'En attente';
      else if (activeTab === 'annulee') matchesActiveTab = reservation.statut === 'Annulée';
      else if (activeTab === 'encours') matchesActiveTab = reservation.statut === 'En cours';
      else if (activeTab === 'terminee') matchesActiveTab = reservation.statut === 'Terminée';
      else if (activeTab === 'voyage') matchesActiveTab = reservation.type === 'Voyage';
      else if (activeTab === 'hotel') matchesActiveTab = reservation.type === 'Hôtel';
      else if (activeTab === 'forfait') matchesActiveTab = reservation.type === 'Forfait';
      
      return matchesSearch && matchesStatus && matchesPeriod && matchesActiveTab;
    });
    
    setFilteredReservations(filtered);
    setPage(0);
  }, [searchTerm, filterStatus, filterPeriod, reservationsData, reservationsLoading, activeTab]);

  // Smart calculation when client, package, or passengers change
  useEffect(() => {
    if (selectedPackage && formData.nombrePassagers > 0) {
      const basePrice = selectedPackage.prix * formData.nombrePassagers;
      const totalPrice = formData.enableTVA 
        ? basePrice * (1 + formData.customTVARate / 100)
        : basePrice;
      const recommendedDeposit = Math.round(totalPrice * 0.3); // 30% deposit
      
      setFormData(prev => ({
        ...prev,
        prixUnitaire: selectedPackage.prix,
        prixTotal: totalPrice,
        acompte: recommendedDeposit,
        destination: selectedPackage.destination,
        forfait: selectedPackage.titre
      }));
    }
  }, [selectedPackage, formData.nombrePassagers, formData.enableTVA, formData.customTVARate]);

  const handleOpenDialog = (reservation = null) => {
    if (reservation) {
      setEditingReservation(reservation);
      setSelectedClient(getClientById(reservation.clientId));
      setSelectedPackage(getPackageById(reservation.forfaitId));
      setFormData({
        clientId: reservation.clientId,
        clientNom: reservation.clientNom,
        clientEmail: reservation.clientEmail,
        destination: reservation.destination,
        forfait: reservation.forfait,
        forfaitId: reservation.forfaitId,
        dateDepart: reservation.dateDepart,
        dateRetour: reservation.dateRetour,
        nombrePassagers: reservation.nombrePassagers,
        prixUnitaire: reservation.prixUnitaire,
        prixTotal: reservation.prixTotal,
        acompte: reservation.acompte,
        statut: reservation.statut,
        statutPaiement: reservation.statutPaiement,
        notes: reservation.notes || '',
        enableTVA: reservation.enableTVA !== undefined ? reservation.enableTVA : true,
        customTVARate: reservation.customTVARate !== undefined ? reservation.customTVARate : settings.taxRate
      });
    } else {
      setEditingReservation(null);
      setSelectedClient(null);
      setSelectedPackage(null);
      setFormData({
        clientId: null,
        clientNom: '',
        clientEmail: '',
        destination: '',
        forfait: '',
        forfaitId: null,
        dateDepart: '',
        dateRetour: '',
        nombrePassagers: 1,
        prixUnitaire: 0,
        prixTotal: 0,
        acompte: 0,
        statut: 'En attente',
        statutPaiement: 'En attente',
        notes: '',
        enableTVA: true,
        customTVARate: settings.taxRate
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReservation(null);
    setSelectedClient(null);
    setSelectedPackage(null);
  };

  const handleSaveReservation = async () => {
    try {
      if (editingReservation && editingReservation.id) {
        await updateResource('reservation', editingReservation.id, formData);
        setSnackbar({ open: true, message: 'Réservation modifiée avec succès', severity: 'success' });
      } else {
        const newRes = await createResource('reservation', {
          ...formData,
          dateReservation: new Date().toISOString()
        });

        // Optimistic UI: ajouter directement à la liste sans attendre le refetch
        setFilteredReservations(prev => [...prev, newRes]);

        setSnackbar({ open: true, message: 'Réservation ajoutée avec succès', severity: 'success' });
      }
      window.dispatchEvent(new CustomEvent('reservationDataUpdate', { detail: { source: 'reservations', action: 'save' } }));
      handleCloseDialog();
    } catch (error) {
      console.error('Save reservation error:', error);
      setSnackbar({ open: true, message: 'Erreur lors de l\'enregistrement de la réservation', severity: 'error' });
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    try {
      // Optimistic UI: retire immédiatement la ligne
      setFilteredReservations(prev => prev.filter(r => r.id !== reservationId));

      await deleteResource('reservation', reservationId);

      // Invalidation/cache rafraîchi via événement global
      window.dispatchEvent(new CustomEvent('reservationDataUpdate', { detail: { source: 'reservations', action: 'delete' } }));

      setSnackbar({ open: true, message: 'Réservation supprimée avec succès', severity: 'success' });
    } catch (error) {
      console.error('Delete reservation error:', error);
      setSnackbar({ open: true, message: `Erreur lors de la suppression de la réservation : ${error.message}`, severity: 'error' });
    } finally {
      setAnchorEl(null);
    }
  };

  const handleMenuClick = (event, reservation) => {
    setAnchorEl(event.currentTarget);
    setSelectedReservation(reservation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReservation(null);
  };

  const handleClientSelection = (event, newValue) => {
    setSelectedClient(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        clientId: newValue.id,
        clientNom: `${newValue.prenom} ${newValue.nom}`,
        clientEmail: newValue.email
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        clientId: null,
        clientNom: '',
        clientEmail: ''
      }));
    }
  };

  const handlePackageSelection = (event, newValue) => {
    setSelectedPackage(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        forfaitId: newValue.id,
        forfait: newValue.titre,
        destination: newValue.destination,
        prixUnitaire: newValue.prix
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        forfaitId: null,
        forfait: '',
        destination: '',
        prixUnitaire: 0
      }));
    }
  };

  const getFidelityColor = (niveau) => {
    switch(niveau) {
      case 'Premium': return 'error';
      case 'Standard': return 'info';
      case 'Bronze': return 'warning';
      default: return 'default';
    }
  };

  const getClientById = (clientId) => {
    // Normalize IDs for consistent comparison
    const normalizedClientId = clientId !== undefined && clientId !== null ? Number(clientId) : null;
    return clientsData.find(client => {
      const normalizedId = client.id !== undefined && client.id !== null ? Number(client.id) : null;
      return normalizedId === normalizedClientId;
    }) || { 
      // Return a default client object if not found
      nom: "Client supprimé", 
      prenom: "", 
      email: "",
      niveauFidelite: "Standard"
    };
  };

  const getPackageById = (packageId) => {
    return packagesData.find(pkg => pkg.id === packageId);
  };

  // Using global formatCurrency from settings context

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Confirmée': return 'success';
      case 'En attente': return 'warning';
      case 'Annulée': return 'error';
      case 'Terminée': return 'info';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (statutPaiement) => {
    switch (statutPaiement) {
      case 'Soldé': return 'success';
      case 'Acompte versé': return 'info';
      case 'En attente': return 'warning';
      case 'Annulé': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'Confirmée': return <CheckCircleIcon />;
      case 'En attente': return <ScheduleIcon />;
      case 'Annulée': return <CancelIcon />;
      case 'Terminée': return <EventIcon />;
      default: return <EventIcon />;
    }
  };

  // Calculate stats for the dashboard cards
  const statsCards = React.useMemo(() => {
    if (isLoading || !reservationsData) {
      return [
        { title: 'Total Réservations', value: '-', icon: EventIcon, color: 'primary', trend: '-' },
        { title: 'Confirmées', value: '-', icon: CheckCircleIcon, color: 'success', trend: '-' },
        { title: 'En attente', value: '-', icon: ScheduleIcon, color: 'warning', trend: '-' },
        { title: 'Chiffre d\'affaires', value: '-', icon: MoneyIcon, color: 'info', trend: '-' }
      ];
    }

    return [
      {
        title: 'Total Réservations',
        value: filteredReservations.length,
        icon: EventIcon,
        color: 'primary',
        trend: '+8%'
      },
      {
        title: 'Confirmées',
        value: filteredReservations.filter(r => r.statut === 'Confirmée').length,
        icon: CheckCircleIcon,
        color: 'success',
        trend: '+12%'
      },
      {
        title: 'En attente',
        value: filteredReservations.filter(r => r.statut === 'En attente').length,
        icon: ScheduleIcon,
        color: 'warning',
        trend: '-5%'
      },
      {
        title: 'Chiffre d\'affaires',
        value: formatCurrency(filteredReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0)),
        icon: MoneyIcon,
        color: 'info',
        trend: '+18%',
        isCurrency: true
      }
    ];
  }, [filteredReservations, isLoading, reservationsData, formatCurrency]);

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportReservationsToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const errorColor = [239, 68, 68]; // red
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Réservations", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      doc.text(`Filtres: Statut: ${filterStatus === 'all' ? 'Tous' : filterStatus} | Période: ${filterPeriod === 'all' ? 'Toutes' : filterPeriod}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Réservations", margin, 58);
      
      // Calculate some key metrics
      const totalReservations = filteredReservations.length;
      const confirmedReservations = filteredReservations.filter(r => r.statut === 'Confirmée').length;
      const pendingReservations = filteredReservations.filter(r => r.statut === 'En attente').length;
      const cancelledReservations = filteredReservations.filter(r => r.statut === 'Annulée').length;
      const totalRevenue = filteredReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0);
      const averageValue = totalReservations > 0 ? totalRevenue / totalReservations : 0;
      
      const stats = [
        { label: "Total des Réservations", value: `${totalReservations}` },
        { label: "Réservations Confirmées", value: `${confirmedReservations} (${Math.round(confirmedReservations / totalReservations * 100 || 0)}%)` },
        { label: "Réservations En Attente", value: `${pendingReservations} (${Math.round(pendingReservations / totalReservations * 100 || 0)}%)` },
        { label: "Réservations Annulées", value: `${cancelledReservations} (${Math.round(cancelledReservations / totalReservations * 100 || 0)}%)` },
        { label: "Chiffre d'Affaires", value: formatCurrencyForPDF(totalRevenue) },
        { label: "Valeur Moyenne", value: formatCurrencyForPDF(averageValue) }
      ];
      
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add destinations summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Top Destinations", margin, doc.lastAutoTable.finalY + 15);
      
      // Calculate top destinations
      const destinationCounts = {};
      filteredReservations.forEach(r => {
        if (!destinationCounts[r.destination]) {
          destinationCounts[r.destination] = { count: 0, revenue: 0 };
        }
        destinationCounts[r.destination].count += 1;
        destinationCounts[r.destination].revenue += (r.prixTotal || 0);
      });
      
      const topDestinations = Object.keys(destinationCounts)
        .map(dest => ({ 
          destination: dest, 
          count: destinationCounts[dest].count,
          revenue: destinationCounts[dest].revenue
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Destination', 'Nombre de Réservations', 'Chiffre d\'Affaires']],
        body: topDestinations.map(d => [
          d.destination, 
          d.count.toString(),
          formatCurrencyForPDF(d.revenue)
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add reservation details
      doc.addPage();
      
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("TravelIT", margin + 6, 17);
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Réservations", pageWidth - margin, 17, { align: 'right' });
      
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(12);
      doc.text("Détails de toutes les réservations", margin, 40);
      
      // Add reservations table
      const reservationsTableData = filteredReservations.map(r => [
        `#${r.id}`,
        r.clientNom || 'N/A',
        r.destination || 'N/A',
        `${new Date(r.dateDepart).toLocaleDateString('fr-FR')} au ${new Date(r.dateRetour).toLocaleDateString('fr-FR')}`,
        r.nombrePassagers || '0',
        formatCurrencyForPDF(r.prixTotal),
        r.statut || 'N/A',
        new Date(r.dateReservation).toLocaleDateString('fr-FR')
      ]);
      
      doc.autoTable({
        startY: 50,
        head: [['Réf.', 'Client', 'Destination', 'Dates', 'Passagers', 'Montant', 'Statut', 'Date Création']],
        body: reservationsTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40 },
          3: { cellWidth: 50 },
          4: { cellWidth: 20 },
          5: { cellWidth: 30 },
          6: { cellWidth: 25 },
          7: { cellWidth: 30 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Réservations", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.appName} - Système de Gestion pour Agence de Voyage`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('rapport-reservations.pdf');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Add this function to handle TVA rate changes
  const handleTVAChange = (event) => {
    const newRate = parseFloat(event.target.value);
    if (!isNaN(newRate) && newRate >= 0 && newRate <= 100) {
      setFormData(prev => {
        const basePrice = prev.prixUnitaire * prev.nombrePassagers;
        const newTotal = prev.enableTVA ? basePrice * (1 + newRate / 100) : basePrice;
        const newDeposit = Math.round(newTotal * 0.3);
        
        return {
          ...prev,
          customTVARate: newRate,
          prixTotal: newTotal,
          acompte: newDeposit
        };
      });
    }
  };
  
  // Add this function to handle enabling/disabling TVA
  const handleToggleTVA = (event) => {
    const enabled = event.target.checked;
    setFormData(prev => {
      const basePrice = prev.prixUnitaire * prev.nombrePassagers;
      const newTotal = enabled ? basePrice * (1 + prev.customTVARate / 100) : basePrice;
      const newDeposit = Math.round(newTotal * 0.3);
      
      return {
        ...prev,
        enableTVA: enabled,
        prixTotal: newTotal,
        acompte: newDeposit
      };
    });
  };

  // Add new function to handle opening the details dialog
  const handleOpenDetailsDialog = (reservation) => {
    setReservationDetails(reservation);
    setDetailsDialogOpen(true);
    handleMenuClose();
  };

  // Add new function to close the details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setReservationDetails(null);
  };

  // Pagination for card view
  const paginatedReservations = filteredReservations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handler functions for reservation actions
  const handleViewReservation = (reservation) => {
    setReservationDetails(reservation);
    setDetailsDialogOpen(true);
  };

  const handleEditReservation = (reservation) => {
    handleOpenDialog(reservation);
  };

  // Define LoadingIndicator component
  const LoadingIndicator = () => (
    <Box sx={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      <CircularProgress color="primary" size={60} />
      <Typography variant="h6" sx={{ color: 'rgba(51,65,85,0.8)' }}>
        Chargement des données...
      </Typography>
    </Box>
  );

  // Define EnhancedStatCard component from Dashboard
  const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={0}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
          color: gradient ? 'white' : 'inherit',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          } : {},
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mb: 0.5, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' }
              }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ 
                  opacity: gradient ? 0.9 : 0.7, 
                  mt: 0.5,
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  label={trend} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: trend.startsWith('+') ? 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                    color: gradient ? 'white' : 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
                color: gradient ? 'white' : 'primary.main',
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 }
              }}
            >
              {icon}
            </Avatar>
          </Box>
          
          {progress && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  Progression
                </Typography>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradient ? 'white' : 'primary.main',
                  }
                }} 
              />
            </Box>
          )}
          
          {chart && (
            <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
              {chart}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );

  // Define FloatingActionCard component
  const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
    return (
      <Card sx={{
        background: `linear-gradient(135deg, ${color}15, white)`,
        border: `1px solid ${color}30`,
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        height: '100%', // Ensure consistent height
      }} onClick={onClick}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          position: 'relative', 
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, md: 2 }, 
            mb: { xs: 1.5, md: 2 },
            flexWrap: 'wrap'
          }}>
            <Avatar className="action-icon" sx={{ 
              background: `linear-gradient(135deg, ${color}, ${color}cc)`,
              width: { xs: 48, sm: 52, md: 56 },
              height: { xs: 48, sm: 52, md: 56 },
              boxShadow: `0 8px 25px ${color}40`,
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 26, sm: 28, md: 30 }
              }
            }}>
              {icon}
            </Avatar>
            {badge && (
              <Chip 
                label={badge} 
                size="small" 
                sx={{ 
                  background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                  color: 'white',
                  fontWeight: 600,
                  height: { xs: '20px', md: '24px' },
                  fontSize: { xs: '0.7rem', md: '0.75rem' }
                }}
              />
            )}
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              mb: { xs: 0.5, md: 1 }, 
              color: 'rgba(51,65,85,0.9)',
              lineHeight: 1.3,
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(51,65,85,0.7)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              display: { xs: '-webkit-box', md: 'block' },
              WebkitLineClamp: { xs: 2, md: 'none' },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }} className="space-y-6">
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
            Gestion des Réservations
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
          }}>
            Gérez vos réservations et voyages clients en temps réel
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            },
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
          }}
        >
          Nouvelle Réservation
        </Button>
      </Box>

      {/* Interface des réservations - affichage immédiat */}
      {/* Quick Actions Section (optional) */}
          {showQuickActions && (
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <RocketLaunch sx={{ mr: 1.5, color: '#6366f1', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Actions Rapides
              </Typography>
            </Box>
            
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => handleOpenDialog()}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#6366f1',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <FlightIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Nouvelle Réservation
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Ajouter une réservation
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
                    },
                    height: '100%'
                  }} onClick={exportReservationsToPDF}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#10b981',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <PrintIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Imprimer Détails
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Imprimer les détails de voyage
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => navigate('/forfaits')}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#f59e0b',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <CardGiftcard />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Forfaits Disponibles
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Parcourir les forfaits
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => navigate('/rapports')}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#8b5cf6',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <BarChart />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Rapports
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Voir les statistiques
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Box>
          )}
          
          {/* Key Metrics Section */}
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AutoAwesome sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Métriques en Temps Réel
              </Typography>
            </Box>
            
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {statsCards.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                        <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                        <p className="text-white/60 text-xs mt-1">
                          {index === 0 ? "Total des voyages" : 
                           index === 1 ? "Confirmées et actives" : 
                           index === 2 ? "En attente de traitement" : 
                           "Revenus générés"}
                        </p>
                        {stat.trend && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white mt-2">
                            {stat.trend}
                          </span>
                        )}
                      </div>
                      <div className={`p-3 rounded-2xl ${index === 0 ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 
                                      index === 1 ? 'bg-gradient-to-r from-green-600 to-green-700' : 
                                      index === 2 ? 'bg-gradient-to-r from-amber-600 to-amber-700' : 
                                      'bg-gradient-to-r from-purple-600 to-purple-700'}`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* Filtration Rapide */}
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Filtration Rapide
              </Typography>
            </Box>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'all'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <CalendarIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">Toutes</span>
                </button>
                <button
                  onClick={() => setActiveTab('confirmee')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'confirmee'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">Confirmées</span>
                </button>
                <button
                  onClick={() => setActiveTab('attente')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'attente'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <ScheduleIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">En Attente</span>
                </button>
                <button
                  onClick={() => setActiveTab('encours')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'encours'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <FlightIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">En Cours</span>
                </button>
                <button
                  onClick={() => setActiveTab('terminee')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'terminee'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <EventIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">Terminées</span>
                </button>
              </div>
            </div>
          </Box>

          {/* Search and Filters Section */}
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SearchIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Recherche de Réservations
              </Typography>
            </Box>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher une réservation..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      },
                      '& .MuiInputBase-input': {
                        color: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}>Statut</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Statut"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      <MenuItem value="En attente">En attente</MenuItem>
                      <MenuItem value="Confirmée">Confirmées</MenuItem>
                      <MenuItem value="Terminée">Terminées</MenuItem>
                      <MenuItem value="Annulée">Annulées</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}>Période</InputLabel>
                    <Select
                      value={filterPeriod}
                      label="Période"
                      onChange={(e) => setFilterPeriod(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Toutes</MenuItem>
                      <MenuItem value="thisMonth">Ce mois</MenuItem>
                      <MenuItem value="nextMonth">Mois prochain</MenuItem>
                      <MenuItem value="upcoming">À venir</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FilterIcon />}
                      sx={{ 
                        borderRadius: 2, 
                        flex: 1,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Filtrer
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        flex: 1,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d97706, #b45309)'
                        },
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}
                    >
                      Rechercher
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>

          {/* Reservations Table Section */}
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ px: { xs: 2, md: 0 } }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FlightIcon sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                  Liste des Réservations
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* View Toggle Buttons */}
                  <Box sx={{ display: 'flex', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: 2, overflow: 'hidden' }}>
                    <Button
                      variant={viewMode === 'table' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('table')}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 1,
                        borderRadius: 0,
                        border: 'none',
                        backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                          border: 'none'
                        }
                      }}
                    >
                      <TableViewIcon fontSize="small" />
                    </Button>
                    <Button
                      variant={viewMode === 'card' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('card')}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 1,
                        borderRadius: 0,
                        border: 'none',
                        backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                          border: 'none'
                        }
                      }}
                    >
                      <CardViewIcon fontSize="small" />
                    </Button>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<ExportIcon />}
                    onClick={exportReservationsToPDF}
                    sx={{ 
                      borderRadius: 2,
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Exporter PDF
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                      },
                      boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                    }}
                  >
                    Nouvelle Réservation
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden relative">
                {/* Table View */}
                {viewMode === 'table' && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-6 py-4 text-left text-white font-semibold">Réservation</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Client</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Voyage</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Dates</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Passagers</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Montant</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                          <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                      {filteredReservations.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center justify-center p-12 text-center">
                              <FlightIcon className="h-16 w-16 text-white/30 mb-4" />
                              <h3 className="text-xl font-semibold text-white mb-2">Aucune réservation trouvée</h3>
                              <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{ 
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                                  },
                                }}
                              >
                                Ajouter une réservation
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredReservations
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((reservation) => (
                            <motion.tr
                              key={reservation.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="hover:bg-white/5 transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-semibold text-white">
                                    #{reservation.id}
                                  </div>
                                  <div className="text-xs text-white/70">
                                    {formatDate(reservation.dateReservation)}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-3">
                                  <div className="h-9 w-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                    {reservation.clientNom?.split(' ').map(n => n[0]).join('') || 'CL'}
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <div className="font-semibold text-white">
                                        {reservation.clientNom || 'Client non défini'}
                                      </div>
                                      {reservation.clientId && getClientById(reservation.clientId) && (
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          getClientById(reservation.clientId).niveauFidelite === 'Premium' 
                                            ? 'bg-red-500/20 text-red-400' 
                                            : getClientById(reservation.clientId).niveauFidelite === 'Standard'
                                            ? 'bg-blue-500/20 text-blue-400'
                                            : 'bg-yellow-500/20 text-yellow-400'
                                        }`}>
                                          {getClientById(reservation.clientId).niveauFidelite}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-white/70">
                                      {reservation.clientEmail}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="flex items-center space-x-1 mb-1">
                                    <LocationIcon className="h-4 w-4 text-white/60" />
                                    <div className="font-semibold text-white">
                                      {reservation.destination}
                                    </div>
                                  </div>
                                  <div className="text-xs text-white/70">
                                    {reservation.forfait}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-semibold text-white">
                                    {formatDate(reservation.dateDepart)}
                                  </div>
                                  <div className="text-xs text-white/70">
                                    au {formatDate(reservation.dateRetour)}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <div className="relative">
                                    <GroupsIcon className="h-5 w-5 text-white/60" />
                                    <span className="absolute -top-2 -right-2 h-4 w-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                                      {reservation.nombrePassagers}
                                    </span>
                                  </div>
                                  <span className="text-white font-medium text-sm">
                                    {reservation.nombrePassagers === 1 ? '1 passager' : `${reservation.nombrePassagers} passagers`}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-semibold text-green-400">
                                    {formatCurrency(reservation.prixTotal)}
                                  </div>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium mt-1 inline-block ${
                                    reservation.statutPaiement === 'Payé' 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : reservation.statutPaiement === 'En attente'
                                      ? 'bg-yellow-500/20 text-yellow-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {reservation.statutPaiement}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  reservation.statut === 'Confirmée' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : reservation.statut === 'En attente'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : reservation.statut === 'Annulée'
                                    ? 'bg-red-500/20 text-red-400'
                                    : reservation.statut === 'Terminée'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {reservation.statut}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleOpenDetailsDialog(reservation)}
                                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                    title="Voir les détails"
                                  >
                                    <VisibilityIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleOpenDialog(reservation)}
                                    className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                    title="Modifier"
                                  >
                                    <EditIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteReservation(reservation.id)}
                                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                    title="Supprimer"
                                  >
                                    <DeleteIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          ))
                      )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Card View */}
                {viewMode === 'card' && (
                  <div className="p-6">
                    {filteredReservations.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="flex flex-col items-center">
                          <FlightIcon className="h-16 w-16 text-white/40 mb-4" />
                          <h3 className="text-xl font-medium text-white/60 mb-2">Aucune réservation trouvée</h3>
                          <p className="text-white/40 text-lg">
                            {searchTerm || filterStatus !== 'all' 
                              ? 'Essayez de modifier vos critères de recherche.' 
                              : 'Commencez par ajouter votre première réservation.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <Grid container spacing={3}>
                        {paginatedReservations.map((reservation, index) => (
                          <Grid item xs={12} sm={6} lg={4} key={reservation.id}>
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <Card sx={{ 
                                background: 'rgba(255, 255, 255, 0.05)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 3,
                                p: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 12px 24px rgba(139, 92, 246, 0.2)',
                                  background: 'rgba(255, 255, 255, 0.08)'
                                }
                              }}>
                                {/* Reservation Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                  <Avatar
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                      fontSize: '1.2rem',
                                      fontWeight: 600,
                                      mr: 2
                                    }}
                                  >
                                    #{reservation.id}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ 
                                      color: 'white', 
                                      fontWeight: 600, 
                                      fontSize: '1rem',
                                      lineHeight: 1.2,
                                      mb: 0.5
                                    }}>
                                      {reservation.destination || 'Destination inconnue'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                      {reservation.clientNom || 'Client non renseigné'}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Travel Info */}
                                <Box sx={{ mb: 3, flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <CalendarIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)', 
                                      fontSize: '0.85rem'
                                    }}>
                                      Du {formatDate(reservation.dateDepart)} au {formatDate(reservation.dateRetour)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <GroupsIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)', 
                                      fontSize: '0.85rem' 
                                    }}>
                                      {reservation.nombrePassagers || 0} passager{(reservation.nombrePassagers || 0) > 1 ? 's' : ''}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <MoneyIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'white', 
                                      fontSize: '0.9rem', 
                                      fontWeight: 600 
                                    }}>
                                      {formatCurrency(reservation.prixTotal)}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Status and Payment */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={reservation.statut || 'En attente'}
                                    size="small"
                                    sx={{
                                      backgroundColor: getStatusColor(reservation.statut) === 'success' ? 'rgba(34, 197, 94, 0.8)' : 
                                      getStatusColor(reservation.statut) === 'warning' ? 'rgba(245, 158, 11, 0.8)' :
                                      getStatusColor(reservation.statut) === 'error' ? 'rgba(239, 68, 68, 0.8)' :
                                      getStatusColor(reservation.statut) === 'info' ? 'rgba(59, 130, 246, 0.8)' : 'rgba(107, 114, 128, 0.8)',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                  <Chip
                                    label={reservation.statutPaiement || 'Non payé'}
                                    size="small"
                                    sx={{
                                      backgroundColor: getPaymentStatusColor(reservation.statutPaiement) === 'success' ? 'rgba(34, 197, 94, 0.8)' : 
                                      getPaymentStatusColor(reservation.statutPaiement) === 'info' ? 'rgba(59, 130, 246, 0.8)' :
                                      getPaymentStatusColor(reservation.statutPaiement) === 'warning' ? 'rgba(245, 158, 11, 0.8)' :
                                      getPaymentStatusColor(reservation.statutPaiement) === 'error' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(107, 114, 128, 0.8)',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </Box>

                                {/* Actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
                                  <Tooltip title="Voir les détails">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewReservation(reservation)}
                                      sx={{
                                        color: 'rgb(34, 197, 94)',
                                        bgcolor: 'rgba(34, 197, 94, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
                                        width: 36,
                                        height: 36
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Modifier">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditReservation(reservation)}
                                      sx={{
                                        color: 'rgb(245, 158, 11)',
                                        bgcolor: 'rgba(245, 158, 11, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
                                        width: 36,
                                        height: 36
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Supprimer">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteReservation(reservation)}
                                      sx={{
                                        color: 'rgb(239, 68, 68)',
                                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                                        width: 36,
                                        height: 36
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </div>
                )}
                <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                  <div className="text-sm text-white/60">
                    Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredReservations.length)} sur {filteredReservations.length} réservations
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setPage(Math.min(Math.ceil(filteredReservations.length / rowsPerPage) - 1, page + 1))}
                      disabled={page >= Math.ceil(filteredReservations.length / rowsPerPage) - 1}
                      className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              </div>
              </motion.div>
            </Grid>
          </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }
        }}
      >
        <MenuListItem onClick={() => { handleOpenDialog(selectedReservation); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={() => handleOpenDetailsDialog(selectedReservation)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={() => { handleDeleteReservation(selectedReservation?.id); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'white' }}>
            {editingReservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Enhanced Client Selection */}
            <Grid item xs={12}>
              <Autocomplete
                value={selectedClient}
                onChange={handleClientSelection}
                options={clientsData}
                getOptionLabel={(option) => `${option.prenom} ${option.nom} - ${option.email}`}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sélectionner un client" 
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      }
                    }}
                  />
                )}
                renderOption={(optionProps, option) => (
                  <Box component="li" {...optionProps}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                        {option.prenom[0]}{option.nom[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                        {option.prenom} {option.nom}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {option.email} • {option.adresse}
                        </Typography>
                        <Chip 
                          label={option.niveauFidelite}
                          size="small"
                          color={getFidelityColor(option.niveauFidelite)}
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {(option.preferencesVoyage || []).map((pref, index) => (
                          <Chip 
                            key={index}
                            label={pref}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ 
                              borderRadius: 2, 
                              fontSize: '0.7rem',
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: 'rgba(255, 255, 255, 0.8)'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                PaperComponent={({ children, ...props }) => (
                  <Paper 
                    {...props} 
                    sx={{ 
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      '& .MuiAutocomplete-option': {
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)'
                          }
                        }
                      }
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />
            </Grid>

            {/* Enhanced Package Selection */}
            <Grid item xs={12}>
              <Autocomplete
                value={selectedPackage}
                onChange={handlePackageSelection}
                options={packagesData}
                getOptionLabel={(option) => `${option.titre} - ${option.destination}`}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sélectionner un forfait" 
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FlightIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      }
                    }}
                  />
                )}
                renderOption={(optionProps, option) => (
                  <Box component="li" {...optionProps}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main', mr: 1 }}>
                        <FlightIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                          {option.titre}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4ade80' }}>
                          {formatCurrency(option.prix)}/pers
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {option.destination} • {option.duree}
                        </Typography>
                        <Chip 
                          label={option.categorie}
                          size="small"
                          color="info"
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'rgba(255, 255, 255, 0.6)' }}>
                        {(() => {
                          // Ensure inclusions is always an array
                          let inclusions = option.inclusions;
                          if (typeof inclusions === 'string') {
                            try {
                              inclusions = JSON.parse(inclusions);
                            } catch {
                              inclusions = [inclusions];
                            }
                          }
                          if (!Array.isArray(inclusions)) {
                            inclusions = inclusions ? [inclusions] : [];
                          }
                          return inclusions.slice(0, 3).join(' • ') + (inclusions.length > 3 ? '...' : '');
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                )}
                PaperComponent={({ children, ...props }) => (
                  <Paper 
                    {...props} 
                    sx={{ 
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      '& .MuiAutocomplete-option': {
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)'
                          }
                        }
                      }
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />
            </Grid>
            {/* Add TVA Controls here - always visible */}
            <Grid item xs={12}>
              <Card sx={{ 
                background: 'rgba(34, 197, 94, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(34, 197, 94, 0.2)'
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalculateIcon sx={{ color: 'white' }} />
                    Paramètres de TVA
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px dashed rgba(99, 102, 241, 0.4)',
                    color: 'white'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <FormControl component="fieldset">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Switch
                            checked={formData.enableTVA}
                            onChange={handleToggleTVA}
                            color="primary"
                            inputProps={{ 'aria-label': 'Activer/désactiver TVA' }}
                          />
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                            {formData.enableTVA ? 'TVA activée' : 'TVA désactivée'}
                          </Typography>
                        </Box>
                      </FormControl>
                      
                      {formData.enableTVA && (
                        <TextField
                          label="Taux de TVA (%)"
                          type="number"
                          value={formData.customTVARate}
                          onChange={handleTVAChange}
                          InputProps={{
                            endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}>%</InputAdornment>,
                          }}
                          sx={{ 
                            width: '150px',
                            '& .MuiOutlinedInput-root': { 
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              color: 'white'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.2)'
                            },
                            '& .MuiInputLabel-root': {
                              color: 'rgba(255, 255, 255, 0.7)'
                            }
                          }}
                          inputProps={{ min: 0, max: 100, step: 0.5 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Le taux de TVA par défaut est de {settings.taxRate}%. Vous pouvez le modifier pour cette réservation.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de départ"
                type="date"
                value={formData.dateDepart}
                onChange={(e) => setFormData({...formData, dateDepart: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de retour"
                type="date"
                value={formData.dateRetour}
                onChange={(e) => setFormData({...formData, dateRetour: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nombre de passagers"
                type="number"
                value={formData.nombrePassagers}
                onChange={(e) => setFormData({...formData, nombrePassagers: parseInt(e.target.value)})}
                inputProps={{ min: 1 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            {/* Smart Pricing Calculator - keep the existing code but remove the TVA controls from here */}
            {selectedPackage && formData.nombrePassagers > 0 && (
              <Grid item xs={12}>
                <Card sx={{ 
                  background: 'rgba(34, 197, 94, 0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(34, 197, 94, 0.2)'
                }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalculateIcon />
                      Calcul automatique des prix
                    </Typography>
                    
                    {/* Remove TVA Controls from here, as they are now in a separate section */}
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Prix unitaire</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {formatCurrency(formData.prixUnitaire)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Sous-total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                            {formatCurrency(formData.prixUnitaire * formData.nombrePassagers)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {formData.enableTVA ? `Taxes (${formData.customTVARate}%)` : 'Taxes (0%)'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                            {formatCurrency(formData.enableTVA 
                              ? formData.prixTotal - (formData.prixUnitaire * formData.nombrePassagers) 
                              : 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Total TTC</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {formatCurrency(formData.prixTotal)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`Acompte recommandé (${formData.prixTotal ? Math.round((formData.acompte / formData.prixTotal) * 100) : 30}%)`}
                type="number"
                value={formData.acompte}
                onChange={(e) => setFormData({...formData, acompte: parseFloat(e.target.value)})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PaymentIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.6)'
                  }
                }}
                helperText={formData.acompte > 0 ? `Restant à payer: ${formatCurrency(formData.prixTotal - formData.acompte)}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                background: 'rgba(124, 58, 237, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(124, 58, 237, 0.2)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                  Récapitulatif financier
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'white' }}>Total voyage:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                    {formatCurrency(formData.prixTotal)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'white' }}>Acompte:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCurrency(formData.acompte)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white' }}>Solde restant:</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatCurrency(formData.prixTotal - formData.acompte)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut de la réservation</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut de la réservation"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Confirmée">Confirmée</MenuItem>
                  <MenuItem value="Terminée">Terminée</MenuItem>
                  <MenuItem value="Annulée">Annulée</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut du paiement</InputLabel>
                <Select
                  value={formData.statutPaiement}
                  label="Statut du paiement"
                  onChange={(e) => setFormData({...formData, statutPaiement: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Acompte versé">Acompte versé</MenuItem>
                  <MenuItem value="Soldé">Soldé</MenuItem>
                  <MenuItem value="Annulé">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, color: 'white' }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveReservation}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            {editingReservation ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FlightIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                Réservation #{reservationDetails?.id}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Créée le {reservationDetails && formatDate(reservationDetails.dateReservation)}
              </Typography>
            </Box>
            <Box sx={{ 
              px: 2, 
              py: 1, 
              borderRadius: 2, 
              background: reservationDetails?.statut === 'Confirmée' ? 'rgba(34, 197, 94, 0.2)' : 
                         reservationDetails?.statut === 'En attente' ? 'rgba(251, 191, 36, 0.2)' : 
                         'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${reservationDetails?.statut === 'Confirmée' ? 'rgba(34, 197, 94, 0.3)' : 
                                 reservationDetails?.statut === 'En attente' ? 'rgba(251, 191, 36, 0.3)' : 
                                 'rgba(239, 68, 68, 0.3)'}`
            }}>
              <Typography variant="body2" sx={{ 
                color: reservationDetails?.statut === 'Confirmée' ? '#22c55e' : 
                       reservationDetails?.statut === 'En attente' ? '#fbbf24' : 
                       '#ef4444',
                fontWeight: 600
              }}>
                {reservationDetails?.statut || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          {reservationDetails && (
            <Grid container spacing={3}>
              {/* Client Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <PersonIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                      Informations Client
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    mb: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: 'white'
                      }}>
                        {reservationDetails.clientNom?.split(' ').map(n => n[0]).join('') || 'CL'}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                          {reservationDetails.clientNom || 'Client non défini'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {reservationDetails.clientEmail}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {getClientById(reservationDetails.clientId) && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Téléphone
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                            {getClientById(reservationDetails.clientId).telephone}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Niveau de fidélité
                          </Typography>
                          <Box sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            background: 'rgba(251, 191, 36, 0.2)',
                            border: '1px solid rgba(251, 191, 36, 0.3)'
                          }}>
                            <Typography variant="body2" sx={{ color: '#fbbf24', fontWeight: 600 }}>
                              {getClientById(reservationDetails.clientId).niveauFidelite || 'Standard'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              
              {/* Travel Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <FlightIcon sx={{ color: '#10b981', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                      Détails du Voyage
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    mb: 2
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Destination
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                        {reservationDetails.destination || 'Non définie'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Forfait
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                        {reservationDetails.forfait || 'Non défini'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Dates de voyage
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                        {reservationDetails.dateDepart && reservationDetails.dateRetour ? 
                          `${formatDate(reservationDetails.dateDepart)} - ${formatDate(reservationDetails.dateRetour)}` :
                          'Non définies'
                        }
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Nombre de passagers
                      </Typography>
                      <Box sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.3)'
                      }}>
                        <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                          {reservationDetails.nombrePassagers || 1} passager{(reservationDetails.nombrePassagers || 1) > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              {/* Financial Info */}
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <MoneyIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                      Informations Financières
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        p: 2.5, 
                        borderRadius: 2, 
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                          Prix Total
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 600 }}>
                          {reservationDetails.prixTotal || 0} DZD
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        p: 2.5, 
                        borderRadius: 2, 
                        background: 'rgba(251, 191, 36, 0.1)',
                        border: '1px solid rgba(251, 191, 36, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                          Statut Paiement
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#fbbf24', fontWeight: 600 }}>
                          {reservationDetails.statutPaiement || 'En attente'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2, 
                  background: 'rgba(255, 255, 255, 0.1)', 
                  border: '1px solid rgba(255, 255, 255, 0.2)' 
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ 
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', 
                        width: 48, 
                        height: 48 
                      }}>
                        <FlightIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                          {reservationDetails.destination}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {reservationDetails.forfait}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Dates de voyage
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {formatDate(reservationDetails.dateDepart)} au {formatDate(reservationDetails.dateRetour)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Nombre de passagers
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {reservationDetails.nombrePassagers}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Prix unitaire
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {formatCurrency(reservationDetails.prixUnitaire)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Financial Info */}
              <Grid item xs={12}>
                <Card sx={{ 
                  borderRadius: 2, 
                  background: 'rgba(255, 255, 255, 0.1)', 
                  border: '1px solid rgba(255, 255, 255, 0.2)' 
                }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                      Détails financiers
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: 2, 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)', 
                          borderRadius: 2 
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Sous-total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            {formatCurrency(reservationDetails.prixUnitaire * reservationDetails.nombrePassagers)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: 2, 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)', 
                          borderRadius: 2 
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {reservationDetails.enableTVA !== undefined ? 
                              (reservationDetails.enableTVA ? `TVA (${reservationDetails.customTVARate || settings.taxRate}%)` : 'TVA (0%)') : 
                              `TVA (${settings.taxRate}%)`
                            }
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            {formatCurrency(
                              reservationDetails.prixTotal - (reservationDetails.prixUnitaire * reservationDetails.nombrePassagers)
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: 2, 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)', 
                          borderRadius: 2 
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Total TTC</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#4ade80' }}>
                            {formatCurrency(reservationDetails.prixTotal)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: 2, 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)', 
                          borderRadius: 2 
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Statut de paiement</Typography>
                          <Chip 
                            label={reservationDetails.statutPaiement}
                            sx={{ 
                              mt: 1, 
                              borderRadius: 2,
                              backgroundColor: reservationDetails.statutPaiement === 'Payé' 
                                ? 'rgba(34, 197, 94, 0.2)' 
                                : 'rgba(245, 158, 11, 0.2)',
                              color: reservationDetails.statutPaiement === 'Payé' ? '#4ade80' : '#fbbf24',
                              border: `1px solid ${reservationDetails.statutPaiement === 'Payé' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ 
                      mt: 3, 
                      p: 2, 
                      background: 'rgba(255, 255, 255, 0.1)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)', 
                      borderRadius: 2 
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>Acompte versé</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#4ade80' }}>
                          {formatCurrency(reservationDetails.acompte)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>Solde restant</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#f87171' }}>
                          {formatCurrency(reservationDetails.prixTotal - reservationDetails.acompte)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Notes */}
              {reservationDetails.notes && (
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {reservationDetails.notes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDetailsDialog} 
            variant="outlined" 
            sx={{ 
              borderRadius: 2,
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Fermer
          </Button>
          <Button 
            onClick={() => { handleOpenDialog(reservationDetails); handleCloseDetailsDialog(); }}
            variant="contained" 
            startIcon={<EditIcon />}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
              }
            }}
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reservations; 